/**
 * features/selfservice/useMyStaffData.ts
 * ------------------------------------------------------------------
 * Staff self-service data layer. Given the signed-in user's linked
 * staffId, subscribes to ONLY that staff member's own documents —
 * their staff record, payslips, leave requests and leave allocation.
 *
 * Every subscription is constrained to the one staffId (a `where`
 * filter or a direct doc id), so it matches the scoped Firestore
 * rules: a self-service user can read their own HR docs and nothing
 * else. This is deliberately separate from useHrData (which loads the
 * whole HR domain for HR staff).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { ENCASHMENT_COLLECTION, type HrLeaveEncashment } from '@/lib/leaveEncashment';
import { db } from '@/lib/firebase';
import { HR_COLLECTIONS, type HrStaff, type HrLeaveRequest } from '@/lib/hr';
import { PAYROLL_COLLECTIONS, type HrPayslip, type HrLeaveAllocation } from '@/lib/hrPayroll';

export interface MyStaffData {
  staff?: HrStaff;
  payslips: HrPayslip[];
  leave: HrLeaveRequest[];
  /** Their own sales of leave days. */
  encashments: HrLeaveEncashment[];
  allocation?: HrLeaveAllocation;
  loading: boolean;
  error: string | null;
}

export function useMyStaffData(staffId?: string): MyStaffData {
  const [staff, setStaff] = useState<HrStaff | undefined>(undefined);
  const [payslips, setPayslips] = useState<HrPayslip[]>([]);
  const [leave, setLeave] = useState<HrLeaveRequest[]>([]);
  const [allocation, setAllocation] = useState<HrLeaveAllocation | undefined>(undefined);
  const [encashments, setEncashments] = useState<HrLeaveEncashment[]>([]);
  const [ready, setReady] = useState({ staff: false, payslips: false, leave: false, allocation: false });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!staffId) {
      setStaff(undefined); setPayslips([]); setLeave([]); setAllocation(undefined); setEncashments([]);
      setReady({ staff: true, payslips: true, leave: true, allocation: true });
      return;
    }
    setReady({ staff: false, payslips: false, leave: false, allocation: false });
    const onErr = (e: { message: string }) => setError(e.message);

    const unsubStaff = onSnapshot(
      doc(db, HR_COLLECTIONS.staff, staffId),
      (snap) => { setStaff(snap.exists() ? ({ id: snap.id, ...snap.data() } as HrStaff) : undefined); setReady((r) => ({ ...r, staff: true })); },
      onErr,
    );
    const unsubAlloc = onSnapshot(
      doc(db, PAYROLL_COLLECTIONS.leaveAllocations, staffId),
      (snap) => { setAllocation(snap.exists() ? ({ id: snap.id, ...snap.data() } as HrLeaveAllocation) : undefined); setReady((r) => ({ ...r, allocation: true })); },
      onErr,
    );
    const unsubSlips = onSnapshot(
      query(collection(db, PAYROLL_COLLECTIONS.payslips), where('staffId', '==', staffId)),
      (snap) => { setPayslips(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HrPayslip)); setReady((r) => ({ ...r, payslips: true })); },
      onErr,
    );
    const unsubLeave = onSnapshot(
      query(collection(db, HR_COLLECTIONS.leave), where('staffId', '==', staffId)),
      (snap) => { setLeave(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HrLeaveRequest)); setReady((r) => ({ ...r, leave: true })); },
      onErr,
    );

    /* Their own sales, so self-service shows the same balance HR does. Not
       part of `loading`, and a refused read is an empty list rather than an
       error banner: a member of staff who has never sold a day must not be
       shown a failure because of a feature they have not used. */
    const unsubEnc = onSnapshot(
      query(collection(db, ENCASHMENT_COLLECTION), where('staffId', '==', staffId)),
      (snap) => setEncashments(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HrLeaveEncashment)),
      () => setEncashments([]),
    );

    return () => { unsubStaff(); unsubAlloc(); unsubSlips(); unsubLeave(); unsubEnc(); };
  }, [staffId]);

  const loading = !ready.staff || !ready.payslips || !ready.leave || !ready.allocation;

  return useMemo(
    () => ({ staff, payslips, leave, allocation, encashments, loading, error }),
    [staff, payslips, leave, allocation, encashments, loading, error],
  );
}
